'use strict';
/* ==========================================================================
   core-local/words.js — "It's All Greek (and Latin) to Me": the word model (DESIGN.md §4). Pure: no DOM, no engine
   globals at load time. Runs in node (module.exports: tools/wordscheck.js, tools/contentcheck.js) and in the page
   (window.Words). Registers itself with the question bank as `words` when core/bank.js is loaded first (lesson.json
   scripts order), so pool formulas can say `words.gloss(w)`.

   The honesty rule: every word, split, literal meaning and verdict comes from content/roots.json (a trimmed, deep-equal
   copy of docs/topics/data-roots.json, every entry with its facts row and sources) read through
   content/lesson-words.json (the lesson's choices: which words, their letter segmentation, the tile set, sentences,
   short student meanings written from the stored dictionary meaning). Nothing here states a fact of its own.
   Data: in the page Game.content.roots + Game.content.lesson_words (loaded on the first call); in node the two files
   are required. Words.use(roots, lessonWords) sets them explicitly. API contract for the level agents: WORDS.md.
   ========================================================================== */
(function (root) {
  let R = null, L = null;                 // roots.json, lesson-words.json
  let P = {}, WROW = {}, WORDS = {}, SURP = {}, REJ = {}, REJW = {}, REAL = {}, TILE = {}, BLOCK = [], CROW = {}, DROW = {}, EROW = {};
  let pressMemo = null, buildMemo = null;

  /* ---------------- data ---------------- */
  function use(roots, lw) {
    R = roots; L = lw; pressMemo = null; buildMemo = null;
    P = {}; WROW = {}; WORDS = {}; SURP = {}; REJ = {}; REJW = {}; REAL = {}; TILE = {}; BLOCK = []; CROW = {}; DROW = {}; EROW = {};
    for (const p of R.parts) P[p.id] = p;
    for (const w of R.words) WROW[w.word.toLowerCase()] = w;
    for (const c of R.surprises_C || []) CROW[c.row] = c;
    for (const t of R.traps_D || []) DROW[t.row] = t;
    for (const e of R.myths_E || []) EROW[e.row] = e;
    const F = R.builds_F || {};
    for (const s of F.surprisingly_real || []) SURP[s.word.toLowerCase()] = s;
    for (const r of F.rejected_builds || []) { REJ[r.parts.join('+')] = r; REJW[r.build.toLowerCase()] = r; }
    for (const b of F.blocked || []) BLOCK.push({ word: b.word, parts: blockedParts(b) });
    for (const x of L.exclude || []) BLOCK.push({ word: x.word, parts: x.parts });
    for (const t of L.press.tiles) TILE[t.part] = t;
    for (const [id, e] of Object.entries(L.words)) WORDS[id] = makeWord(id, e);
    // the press's real words: every lesson word whose parts can be pressed (2..max tiles, a clean segmentation)
    const byKey = {};
    for (const w of Object.values(WORDS)) if (w.pressable) (byKey[w.parts.join('+')] = byKey[w.parts.join('+')] || []).push(w);
    for (const [k, ws] of Object.entries(byKey)) {
      const clean = ws.filter(w => w.clean);
      const pick = clean.length === 1 ? clean[0] : clean.length === 0 && ws.length === 1 ? ws[0] : null;
      if (pick) REAL[k] = pick.id;
    }
    for (const [id, s] of Object.entries(L.surprises || {})) REAL[s.tiles.join('+')] = REAL[s.tiles.join('+')] || 'surprise:' + id;
    return Words;
  }
  // "paed + phil: real word ..." / "homo + phob: ..." → part ids (the reason text names them)
  function blockedParts(b) {
    const m = /^([a-z_]+) \+ ([a-z_]+)/.exec(b.reason || '');
    if (!m) return null;
    const id = n => (P[n] ? n : Object.values(P).find(p => p.forms.includes(n) && p.language.startsWith('Greek')) ? Object.values(P).find(p => p.forms.includes(n) && p.language.startsWith('Greek')).id : n);
    return [id(m[1]), id(m[2])];
  }
  function ensure() {
    if (R) return;
    const G = typeof Game !== 'undefined' ? Game : root.Game;
    if (G && G.content && G.content.roots && G.content.lesson_words) { use(G.content.roots, G.content.lesson_words); return; }
    if (typeof require === 'function' && typeof module !== 'undefined') { use(require('../content/roots.json'), require('../content/lesson-words.json')); return; }
    throw new Error('words: no data (Game.content.roots / lesson_words not loaded)');
  }

  /* ---------------- parts ---------------- */
  const LANG = s => {
    s = String(s || '');
    if (/^Greek and Latin|^Latin and Greek/.test(s)) return 'Greek and Latin';
    if (/^Greek/.test(s)) return 'Greek';
    if (/^Latin/.test(s)) return 'Latin';
    if (/^Old English/.test(s)) return 'Old English';
    return s;
  };
  function part(id) {
    ensure();
    const p = P[id];
    if (!p) return null;
    return { id, row: p.row, forms: p.forms.slice(), kind: p.kind, language: LANG(p.language), languageRaw: p.language, origin: p.origin,
      meaning: p.meaning, short: short(id), label: label(id), tile: !!TILE[id], status: p.status };
  }
  const short = id => { ensure(); return (L.short && L.short[id]) || (P[id] ? P[id].meaning.split(/ \/ |;/)[0] : ''); };
  const label = id => { ensure(); return TILE[id] ? TILE[id].label : P[id] ? P[id].forms[0] : String(id); };
  const kind = p => { ensure(); const x = P[typeof p === 'object' ? p.id : p]; return x ? x.kind : null; };
  const language = p => { ensure(); const x = P[typeof p === 'object' ? p.id : p]; return x ? LANG(x.language) : null; };
  // a build/word is a hybrid when its non-suffix parts include a strictly Greek part and a strictly Latin part (D30)
  function hybrid(ids) {
    ensure();
    const L2 = ids.filter(id => P[id] && P[id].kind !== 'suffix').map(id => LANG(P[id].language));
    return L2.includes('Greek') && L2.includes('Latin');
  }

  /* ---------------- words ---------------- */
  const cleanGloss = g => String(g || '').replace(/\s*\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
  function makeWord(id, e) {
    const w = WROW[id];
    if (!w) throw new Error(`words: "${id}" is not a row of the word table`);
    const seg = (e.seg || []).map(x => ({ t: x[0], part: x[1] === '+' || x[1] === '.' ? null : x[1], link: x[1] === '+', end: x[1] === '.' }));
    let at = 0;
    for (const s of seg) { s.start = at; at += s.t.length; s.stop = at; }
    const endLetters = seg.filter(s => s.end).map(s => s.t).join('');
    const max = (L.press && L.press.max) || 3, min = (L.press && L.press.min) || 2;
    return {
      id, word: w.word, row: w.row, status: w.status, parts: w.parts.slice(), seg, endLetters,
      gloss: cleanGloss(w.gloss), literal: w.gloss, meaning: e.meaning === undefined ? null : e.meaning, dict: w.meaning,
      sentence: e.sentence || null, foils: e.foils || [], sound: e.sound || null, context: e.context || null,
      hybrid: hybrid(w.parts), surprise: !!SURP[id],
      cuttable: seg.length > 0 && seg.filter(s => s.part).length >= 2,
      pressable: w.parts.length >= min && w.parts.length <= max,
      clean: seg.length > 0 && !endLetters,
      scored: w.status === 'VERIFIED',
    };
  }
  function word(id) {
    ensure();
    id = String(id).toLowerCase();
    if (WORDS[id]) return WORDS[id];
    if (L.other && L.other[id]) {
      const o = L.other[id], row = DROW[o.src] || CROW[o.src];
      const txt = row ? JSON.stringify(row) : '';
      const m = new RegExp('\\b(' + id + ')\\b', 'i').exec(txt);
      return { id, word: m ? m[1] : id, row: o.src, status: row ? row.status : null, parts: [], seg: [], endLetters: '', gloss: null, literal: null,
        meaning: o.meaning || null, dict: null, sentence: o.sentence || null, foils: [], sound: null, context: o.context || null, hybrid: false,
        surprise: false, cuttable: false, pressable: false, scored: !!row && row.status === 'VERIFIED', other: true };
    }
    return null;
  }
  const W = id => { const w = word(id); if (!w) throw new Error(`words: no word "${id}"`); return w; };
  const parts = id => W(id).parts.slice();
  const gloss = id => { const w = word(id); if (w) return w.gloss; const b = entry(id); return b ? b.gloss : null; };
  const meaning = id => { const w = word(id); if (w) return w.meaning; const s = L.surprises && L.surprises[String(id).toLowerCase()]; return s ? s.meaning : null; };
  const spell = id => { const w = word(id); if (w) return w.word; const b = entry(id); return b ? b.word : String(id); };
  const segments = id => W(id).seg.map(s => ({ ...s }));
  const sentence = id => W(id).sentence;

  /* ---------------- cutting ---------------- */
  // the boundaries between parts: [{allowed: [positions], at: canonical position}] (a cut at position p falls between
  // letters p-1 and p). A linking letter allows a cut on either side; letters marked "." end the word: no cut there.
  function boundaries(id) {
    const s = W(id).seg, out = [];
    for (let i = 0; i < s.length; i++) {
      if (!s[i].part) continue;
      let j = i + 1;
      if (j < s.length && s[j].link && j + 1 < s.length && s[j + 1].part) out.push({ allowed: [s[j].start, s[j].stop], at: s[j].stop, left: s[i].part, right: s[j + 1].part });
      else if (j < s.length && s[j].part) out.push({ allowed: [s[j].start], at: s[j].start, left: s[i].part, right: s[j].part });
    }
    return out;
  }
  const cuts = id => boundaries(id).map(b => b.at);
  const cutSets = id => boundaries(id).map(b => b.allowed.slice());
  // where does position p land? the segment containing letters p-1 and p (a part, a link or the ending)
  function inside(id, p) {
    const s = W(id).seg;
    for (const x of s) if (x.start < p && p < x.stop) return x;
    return null;
  }
  function checkCut(id, positions) {
    const w = W(id), B = boundaries(id);
    const pos = [...new Set((positions || []).map(Number))].filter(p => p > 0 && p < w.word.length).sort((a, b) => a - b);
    const hit = B.map(() => 0), wrong = [];
    for (const p of pos) {
      const j = B.findIndex(b => b.allowed.includes(p));
      if (j >= 0 && !hit[j]) { hit[j] = 1; continue; }
      const x = inside(id, p);
      wrong.push({ at: p, inside: x && x.part ? x.part : null, text: x ? x.t : null, ending: !!(x && x.end) || (!x && w.seg.some(s => s.end && s.start === p)),
        link: !!(x && x.link) || j >= 0, sound: !!(w.sound && w.sound.includes(p)) });
    }
    const missing = hit.filter(h => !h).length;
    return { right: wrong.length === 0 && missing === 0, missing, wrong, sound: wrong.some(x => x.sound), cuts: B.map(b => b.at), allowed: B.map(b => b.allowed.slice()) };
  }
  // the word cut at the given positions (default: the right cuts), pieces joined by " + " (a bar reads as a slash in marker caps)
  function cutText(id, positions) {
    const w = W(id), ps = (positions || cuts(id)).slice().sort((a, b) => a - b), out = [];
    let last = 0;
    for (const p of ps) { out.push(w.word.slice(last, p)); last = p; }
    out.push(w.word.slice(last));
    return out.join(' + ');
  }
  // wrong cut sets, in a fixed order: the recorded by-sound cut first, then each boundary moved 1, then 2 letters
  function wrongCuts(id) {
    const w = W(id), B = boundaries(id), right = B.map(b => b.at), ok = new Set(B.flatMap(b => b.allowed)), out = [], seen = new Set();
    const add = arr => { const k = arr.join(','); if (seen.has(k)) return; if (arr.some((p, i) => B[i].allowed.includes(p)) && arr.every((p, i) => B[i].allowed.includes(p))) return; seen.add(k); out.push(arr); };
    if (w.sound && w.sound.length === B.length) add(w.sound.slice());
    for (const d of [-1, 1, -2, 2]) for (let j = 0; j < B.length; j++) {
      const arr = right.slice(); arr[j] = right[j] + d;
      const lo = j ? arr[j - 1] : 0, hi = j < B.length - 1 ? arr[j + 1] : w.word.length;
      if (arr[j] <= lo || arr[j] >= hi || ok.has(arr[j])) continue;
      add(arr);
    }
    return out;
  }
  const wrongCutText = (id, n) => { const v = wrongCuts(id)[n]; return v ? cutText(id, v) : ''; };
  function wrongCutWhy(id, n) {
    const v = wrongCuts(id)[n], w = W(id);
    if (!v) return '';
    if (w.sound && v.join(',') === w.sound.join(',')) return `That's where it sounds right. The parts split at ${cutText(id)}.`;
    const bad = v.find(p => !boundaries(id).some(b => b.allowed.includes(p)));
    const x = inside(id, bad);
    if (x && x.part) return `That cuts into ${x.t}, one part: ${short(x.part)}. Split it ${cutText(id)}.`;
    return `That cut misses the parts. Split it ${cutText(id)}.`;
  }
  // the span of the part of a given kind ('prefix' | 'root' | 'suffix'), when the word has exactly one
  function piece(id, k) {
    const ps = W(id).seg.filter(s => s.part && P[s.part].kind === k);
    return ps.length === 1 ? ps[0].t : '';
  }
  function pieceWhy(id, k) {
    const t = piece(id, k);
    if (k === 'prefix') return `${t} goes on the front of the word: it is the prefix.`;
    if (k === 'suffix') return `${t} goes on the end of the word: it is the suffix.`;
    return `${t} carries the main meaning: it is the root.`;
  }

  /* ---------------- the Word Press ---------------- */
  const tiles = () => { ensure(); return L.press.tiles.map(t => ({ part: t.part, label: t.label, short: short(t.part), language: LANG(P[t.part].language), kind: P[t.part].kind })); };
  const toIds = seq => (seq || []).map(t => (t && typeof t === 'object' ? t.part : String(t)));
  function refused(ids) {
    for (const b of BLOCK) {
      if (!b.parts) continue;
      for (let i = 0; i + b.parts.length <= ids.length; i++) if (b.parts.every((p, k) => ids[i + k] === p)) return b.word;
    }
    return null;
  }
  // build(tiles) → {verdict: 'real' | 'not-in-dictionary' | 'not-on-list', word, gloss, hybrid, surprise, row, meaning, parts}
  // or null: fewer than press.min / more than press.max tiles, an unknown part, or a blocked combination (refused silently)
  function build(seq) {
    ensure();
    const ids = toIds(seq);
    if (ids.length < L.press.min || ids.length > L.press.max || ids.some(id => !P[id])) return null;
    if (refused(ids)) return null;
    const key = ids.join('+'), hy = hybrid(ids), real = REAL[key];
    if (real && real.startsWith('surprise:')) {
      const id = real.slice(9), s = SURP[id];
      return { verdict: 'real', word: id, gloss: ids.map(short).join(' + '), hybrid: hy, surprise: true, row: 'F', meaning: L.surprises[id].meaning, parts: ids, id, evidence: s ? s.evidence : null };
    }
    if (real) { const w = WORDS[real]; return { verdict: 'real', word: w.word, gloss: w.gloss, hybrid: w.hybrid, surprise: w.surprise, row: w.row, meaning: w.meaning, parts: ids, id: w.id }; }
    if (REJ[key]) { const r = REJ[key]; return { verdict: 'not-in-dictionary', word: r.build, gloss: r.literal, hybrid: hy, surprise: false, row: 'F', meaning: null, parts: ids, id: r.build }; }
    return { verdict: 'not-on-list', word: ids.map(label).join(''), gloss: ids.map(short).join(' + '), hybrid: hy, surprise: false, row: null, meaning: null, parts: ids, id: null };
  }
  // every tile sequence the press accepts (min..max tiles, repeats allowed) → its build; memoized
  function pressAll() {
    if (pressMemo) return pressMemo;
    ensure();
    const T = L.press.tiles.map(t => t.part), out = [];
    const rec = seq => {
      if (seq.length >= L.press.min) out.push({ seq: seq.slice(), b: build(seq) });
      if (seq.length === L.press.max) return;
      for (const t of T) { seq.push(t); rec(seq); seq.pop(); }
    };
    rec([]);
    return (pressMemo = out);
  }
  // memoized (the tables never change after use()): the press's 70 602 sequences are walked once; callers get a copy
  const buildable = () => (buildMemo || (buildMemo = [...new Set(pressAll().filter(x => x.b && x.b.verdict === 'real').map(x => x.b.id))].sort())).slice();
  const reachableNotInDictionary = () => [...new Set(pressAll().filter(x => x.b && x.b.verdict === 'not-in-dictionary').map(x => x.b.word))].sort();
  const orders = () => { ensure(); return L.orders.map(id => { const w = W(id); return { id, word: w.word, tiles: w.parts.slice(), gloss: w.gloss, meaning: w.meaning, row: w.row }; }); };
  // a build entry by id: a lesson word, a surprise, or a checked "not in the dictionary" build
  function entry(b) {
    ensure();
    b = String(b).toLowerCase();
    if (WORDS[b]) return { id: b, word: WORDS[b].word, parts: WORDS[b].parts, verdict: 'real', gloss: WORDS[b].gloss, meaning: WORDS[b].meaning, row: WORDS[b].row };
    if (L.surprises && L.surprises[b]) return { id: b, word: b, parts: L.surprises[b].tiles, verdict: 'real', gloss: L.surprises[b].tiles.map(short).join(' + '), meaning: L.surprises[b].meaning, row: 'F' };
    if (REJW[b]) return { id: b, word: REJW[b].build, parts: REJW[b].parts, verdict: 'not-in-dictionary', gloss: REJW[b].literal, meaning: null, row: 'F' };
    return null;
  }

  /* ---------------- traps and tales ---------------- */
  function trap(id) {
    ensure();
    const t = L.traps[id];
    if (!t) return null;
    return { id, row: t.row, title: t.title,
      bins: t.bins.map(b => ({ id: b.id, label: b.label, part: b.part, say: b.say || null, short: b.part ? short(b.part) : null })),
      words: t.words.map(x => ({ id: x.id, word: spell(x.id), bin: x.bin, sentence: x.sentence, note: x.note || null, row: (word(x.id) || {}).row || t.row })) };
  }
  const traps = () => { ensure(); return Object.keys(L.traps); };
  function tale(id) {
    ensure();
    const t = L.tales[id];
    if (!t) return null;
    const c = CROW[t.row], e = EROW[t.row];
    let truth = null, verdict = null, status = null;
    if (c) { verdict = 'true'; status = c.status; truth = c.say; }
    if (e) { verdict = 'tall'; status = e.status; const m = /Truth:\s*(.*)$/.exec(e.myth_and_truth); truth = m ? m[1].charAt(0).toUpperCase() + m[1].slice(1) : null; }
    return { id, row: t.row, verdict, status, card: t.card, truth, item: c ? c.item : e ? e.item : null };
  }
  const tales = () => { ensure(); return Object.keys(L.tales); };

  /* ---------------- pool helpers (strings; core/bank.js formulas call them as words.x(...)) ---------------- */
  const cap = s => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');
  const foil = (id, n) => W(id).foils[n];
  // the first non-suffix part of a that b does not have → its span in a ({t, part})
  function differ(a, b) {
    const A = W(a), Bp = new Set(word(b) ? word(b).parts : (entry(b) || { parts: [] }).parts);
    const xs = A.seg.filter(s => s.part && !Bp.has(s.part));
    return xs.find(s => P[s.part].kind !== 'suffix') || xs[0] || null;
  }
  const foilMeaning = (id, n) => meaning(foil(id, n)) || '';
  function foilWhy(id, n) {
    const f = foil(id, n), x = differ(id, f);
    return x ? `That's what ${spell(f)} means. In ${spell(id)}, ${x.t} is ${short(x.part)}.` : `That's what ${spell(f)} means.`;
  }
  // "carni + vore": the spans, a linking letter kept with the piece before it, ending letters with the last piece
  function partsText(id) {
    const e = entry(id);
    if (WORDS[String(id).toLowerCase()]) {
      const out = [];
      for (const s of W(id).seg) { if (s.part || !out.length) out.push(s.t); else out[out.length - 1] += s.t; }
      return out.join(' + ');
    }
    return e ? e.parts.map(label).join(' + ') : '';
  }
  const tilesText = id => { const e = entry(id); return e ? e.parts.map(label).join(' + ') : ''; };
  const foilParts = (id, n) => partsText(foil(id, n));
  const partsWhy = (id, n) => { const f = foil(id, n); return `That builds ${spell(f)}: part by part, ${gloss(f)}.`; };
  const foilGloss = (id, n) => gloss(foil(id, n));
  function glossWhy(id, n) {
    const f = foil(id, n), x = differ(id, f);
    return x ? `That's ${spell(f)}. In ${spell(id)}, ${x.t} is ${short(x.part)}.` : `That's ${spell(f)}.`;
  }
  // the missing first piece: "___ + naut"
  function firstPiece(id) { const s = W(id).seg; let t = s[0].t; if (s[1] && s[1].link) t += s[1].t; return t; }
  function restText(id) { const p = partsText(id).split(' + '); return p.slice(1).join(' + '); }
  // other lesson words with the same parts after the first (a different first part): the missing-part options
  function missFoils(id) {
    const w = W(id), same = x => x.parts.length === w.parts.length && x.parts.slice(1).join('+') === w.parts.slice(1).join('+') && x.parts[0] !== w.parts[0] && x.cuttable;
    const order = [...w.foils, ...Object.keys(WORDS).sort()];
    return [...new Set(order)].filter(x => x !== id && WORDS[x] && same(WORDS[x]) && firstPiece(x) !== firstPiece(id));
  }
  const missFoil = (id, n) => { const f = missFoils(id)[n]; return f ? firstPiece(f) : ''; };
  function missWhy(id, n) { const f = missFoils(id)[n]; if (!f) return ''; const s = W(f).seg[0]; return `${firstPiece(f)} is ${short(s.part)}: ${firstPiece(f)} + ${restText(f)} is ${spell(f)}.`; }
  // real or not
  const verdictText = b => (entry(b).verdict === 'real' ? 'In the dictionary' : 'Not in the dictionary');
  const verdictOther = b => (entry(b).verdict === 'real' ? 'Not in the dictionary' : 'In the dictionary');
  function verdictWhy(b) {
    const e = entry(b);
    if (e.verdict === 'real') return e.meaning ? `It is a dictionary word: ${e.meaning.charAt(0).toLowerCase() + e.meaning.slice(1)}.` : 'It is a dictionary word, even if it looks invented.';
    return 'Sensible parts, but no dictionary we checked has it. Real parts do not make a real word.';
  }
  // traps
  function trapEntry(t, id) { ensure(); const T = L.traps[t]; const x = T && T.words.find(y => y.id === id); if (!x) throw new Error(`words: "${id}" is not in trap "${t}"`); return { T, x, bin: T.bins.find(b => b.id === x.bin), other: T.bins.find(b => b.id !== x.bin) }; }
  const trapLabel = (t, id) => trapEntry(t, id).bin.label;
  const trapOther = (t, id) => trapEntry(t, id).other.label;
  function trapWhy(t, id) { const { x, bin } = trapEntry(t, id); return x.note || `In ${spell(id)}, ${bin.say}.`; }
  const trapSentence = (t, id) => trapEntry(t, id).x.sentence;
  // context: the sentence decides
  const C = id => { const w = word(id); if (!w || !w.context) throw new Error(`words: "${id}" has no context entry`); return w; };
  const ctxSentence = id => C(id).context.sentence;
  const ctxMeaning = id => C(id).meaning;
  const ctxMisread = id => C(id).context.misread;
  const ctxFoilId = id => { const ids = Object.keys(L.words).filter(k => L.words[k].context).concat(Object.keys(L.other || {}).filter(k => L.other[k].context)); return ids[(ids.indexOf(id) + 1) % ids.length]; };
  const ctxFoil = id => ctxMeaning(ctxFoilId(id));
  const ctxMisreadWhy = id => 'That is what the parts suggest. The sentence says otherwise: when they disagree, the sentence wins.';
  const ctxFoilWhy = id => `That is closer to ${spell(ctxFoilId(id))}. Read the sentence again.`;
  // tales
  const taleCard = id => tale(id).card;
  const taleVerdict = id => (tale(id).verdict === 'true' ? 'True story' : 'Tall tale');
  const taleOther = id => (tale(id).verdict === 'true' ? 'Tall tale' : 'True story');
  function taleWhy(id) { const t = tale(id); return t.verdict === 'true' ? `True. ${t.truth}` : `Tall tale. The truth: ${t.truth.charAt(0).toLowerCase() + t.truth.slice(1)}`; }
  // Greek or Latin? one part of one word ("aquanaut:aqua")
  const pairWord = p => String(p).split(':')[0];
  const pairPart = p => String(p).split(':')[1];
  const pairSpan = p => { const s = W(pairWord(p)).seg.find(x => x.part === pairPart(p)); return s ? s.t : ''; };
  const pairLang = p => language(pairPart(p));
  const pairOther = p => (pairLang(p) === 'Greek' ? 'Latin' : 'Greek');
  function pairWhy(p) {
    const w = W(pairWord(p)), me = pairPart(p), lang = pairLang(p);
    const o = w.seg.find(x => x.part && x.part !== me && P[x.part].kind !== 'suffix' && ['Greek', 'Latin'].includes(language(x.part)) && language(x.part) !== lang);
    return `${pairSpan(p)} comes from ${lang}` + (o ? `; ${o.t} comes from ${language(o.part)}. A hybrid word.` : '.');
  }
  // which word shares a part?
  const shareSet = () => { ensure(); return L.share || []; };
  const shared = (a, b) => W(a).parts.filter(p => W(b).parts.includes(p) && P[p].kind !== 'suffix');
  const anyShared = (a, b) => W(a).parts.some(p => W(b).parts.includes(p));
  const sharer = id => shareSet().find(x => x !== id && shared(id, x).length > 0) || '';
  const nonSharer = (id, n) => shareSet().filter(x => x !== id && !anyShared(id, x))[n] || '';
  const shareWhy = (id, n) => `${spell(nonSharer(id, n))} has no part of ${spell(id)} (${partsText(id)}).`;
  function shareExplain(id) { const s = sharer(id), p = shared(id, s)[0], a = W(id).seg.find(x => x.part === p), b = W(s).seg.find(x => x.part === p); return `${spell(id)} and ${spell(s)} share a part: ${a.t === b.t ? a.t : a.t + ' and ' + b.t}, ${short(p)}.`; }

  const Words = {
    use, ensure, data: () => { ensure(); return { roots: R, lessonWords: L }; },
    part, short, label, kind, language, hybrid, cleanGloss,
    word, parts, gloss, meaning, spell, segments, sentence, entry,
    boundaries, cuts, cutSets, checkCut, cutText, wrongCuts, wrongCutText, wrongCutWhy, piece, pieceWhy, inside,
    tiles, build, buildable, pressAll, reachableNotInDictionary, refused,
    get orders() { return orders(); },
    trap, traps, tale, tales,
    cap, foil, foilMeaning, foilWhy, partsText, tilesText, foilParts, partsWhy, foilGloss, glossWhy, firstPiece, restText, missFoils, missFoil, missWhy,
    verdictText, verdictOther, verdictWhy, trapLabel, trapOther, trapWhy, trapSentence,
    ctxSentence, ctxMeaning, ctxMisread, ctxFoil, ctxFoilId, ctxMisreadWhy, ctxFoilWhy,
    taleCard, taleVerdict, taleOther, taleWhy, pairWord, pairPart, pairSpan, pairLang, pairOther, pairWhy,
    sharer, nonSharer, shareWhy, shareExplain, shared,
  };
  root.Words = Words;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('words', Words);
  if (typeof module !== 'undefined' && module.exports) module.exports = Words;
})(typeof window !== 'undefined' ? window : globalThis);
